import {
  addDays,
  addHours,
  endOfDay,
  startOfDay,
  setHours,
  setMinutes,
  setSeconds,
  setMilliseconds,
  differenceInDays,
} from "date-fns";

export interface ReleaseConfiguration {
  duration?: number;
  hour?: string;
  chronoUnit?: "DAYS" | "HOURS";
}

export interface RawCancellationPolicyConfiguration {
  minRelease?: string;
  maxRelease?: string;
  minReleaseTimeLimit?: string;
  maxReleaseTimeLimit?: string;
  type: CancellationPolicyConfigurationType;
  penalty: number;
  cancelOnline: boolean;
}

export interface CancellationPolicyConfiguration {
  minRelease?: ReleaseConfiguration;
  maxRelease?: ReleaseConfiguration;
  type: CancellationPolicyConfigurationType;
  penalty: number;
  cancelOnline: boolean;
}

export enum CancellationPolicyConfigurationType {
  FIX_AMOUNT = "FIX_AMOUNT",
  PERCENTAGE = "PERCENTAGE",
  CHARGE_FIRST_NIGHT = "CHARGE_FIRST_NIGHT",
}

export interface MonetaryAmount {
  amount: number;
  currency: string;
}

export interface PenaltyBracket {
  startDate?: Date;
  startHour?: string;
  endDate?: Date;
  endHour?: string;
  penalty: MonetaryAmount;
  type: CancellationPolicyConfigurationType;
  penaltyBase: number;
  canCancel: boolean;
}

export class CancellationPolicyEntity {
  private checkIn: Date;
  private checkOut: Date;
  private cutOffDate: Date;
  private currency: string;
  private firstNightCost: MonetaryAmount;
  private totalRate: MonetaryAmount;
  private configurations: CancellationPolicyConfiguration[];

  constructor(
    checkIn: Date | string,
    checkOut: Date | string,
    cutOffDate: Date | string,
    currency: string,
    firstNightCost: MonetaryAmount,
    totalRate: MonetaryAmount,
    rawConfigurations: RawCancellationPolicyConfiguration[]
  ) {
    this.checkIn = typeof checkIn === "string" ? new Date(checkIn) : checkIn;
    this.checkOut = typeof checkOut === "string" ? new Date(checkOut) : checkOut;
    this.cutOffDate = typeof cutOffDate === "string" ? new Date(cutOffDate) : cutOffDate;
    this.currency = currency;
    this.firstNightCost = firstNightCost;
    this.totalRate = totalRate;
    this.configurations = this.transformConfigurations(rawConfigurations);
  }

  private transformConfigurations(
    rawConfigurations: RawCancellationPolicyConfiguration[]
  ): CancellationPolicyConfiguration[] {
    return rawConfigurations.map(it => {
      const maxReleaseDate = it.maxRelease ? new Date(it.maxRelease) : undefined;
      const minReleaseDate = it.minRelease ? new Date(it.minRelease) : undefined;

      const maxReleaseDuration = maxReleaseDate
        ? differenceInDays(this.checkIn, maxReleaseDate)
        : undefined;
      const minReleaseDuration = minReleaseDate
        ? differenceInDays(this.checkIn, minReleaseDate)
        : undefined;

      const maxRelease: ReleaseConfiguration | undefined =
        maxReleaseDuration !== undefined
          ? {
              hour: it.maxReleaseTimeLimit || "23:59",
              chronoUnit: "DAYS",
              duration: maxReleaseDuration,
            }
          : undefined;

      const minRelease: ReleaseConfiguration | undefined =
        minReleaseDuration !== undefined
          ? {
              hour: it.minReleaseTimeLimit || "23:59",
              chronoUnit: "DAYS",
              duration: minReleaseDuration,
            }
          : undefined;

      return {
        ...it,
        maxRelease,
        minRelease,
      };
    });
  }

  private setDateTimeValues(
    date: Date,
    values: { hours: number; minutes: number; seconds: number; milliseconds: number }
  ): Date {
    let result = new Date(date);
    result = setHours(result, values.hours);
    result = setMinutes(result, values.minutes);
    result = setSeconds(result, values.seconds);
    result = setMilliseconds(result, values.milliseconds);
    return result;
  }

  private calculatePolicyDate(
    releaseConfiguration: ReleaseConfiguration | undefined,
    isEnd: boolean
  ): Date {
    const duration = releaseConfiguration?.duration;
    const time = releaseConfiguration?.hour;
    const chronoUnit = releaseConfiguration?.chronoUnit;

    // when no release configuration is defined it means the configuration is open-ended in that side
    // if it's the start side (maxRelease) then the policy starts at the beginning of the cutoff date
    // if it's the end side (minRelease) then the policy starts ends at the end of the checkout date
    const isDurationValid =
      duration !== undefined && !Number.isNaN(duration) && Number.isFinite(duration);
    let date: Date | undefined;

    if (isDurationValid) {
      date =
        chronoUnit === "DAYS"
          ? addDays(this.checkIn, -duration!!)
          : addHours(this.checkIn, -duration!!);
    } else {
      date = isEnd ? this.checkOut : this.cutOffDate;
    }

    // since we always get a default time of 23:59, we ignore it if there's no valid duration accompanying it
    if (time && isDurationValid) {
      const [hoursStr, minutesStr] = time.split(":");
      return this.setDateTimeValues(date, {
        hours: parseInt(hoursStr),
        minutes: parseInt(minutesStr),
        seconds: 0,
        milliseconds: 0,
      });
    }

    return isEnd ? endOfDay(date) : startOfDay(date);
  }

  private calculatePenalty(
    configuredPenalty: number,
    type: CancellationPolicyConfigurationType
  ): MonetaryAmount {
    switch (type) {
      case CancellationPolicyConfigurationType.FIX_AMOUNT:
        return { amount: configuredPenalty, currency: this.currency };
      case CancellationPolicyConfigurationType.PERCENTAGE:
        return {
          amount: this.totalRate.amount * (configuredPenalty / 100),
          currency: this.currency,
        };
      case CancellationPolicyConfigurationType.CHARGE_FIRST_NIGHT:
        return this.firstNightCost;
      default:
        throw new Error(`Invalid value provided ${JSON.stringify(type)}`);
    }
  }

  private buildSingleBracket(config: CancellationPolicyConfiguration): PenaltyBracket {
    const isEndDurationValid =
      config.minRelease?.duration !== undefined &&
      !Number.isNaN(config.minRelease?.duration) &&
      Number.isFinite(config.minRelease?.duration);

    const startDate = this.calculatePolicyDate(config.maxRelease, false);
    let endDate = isEndDurationValid
      ? this.calculatePolicyDate(config.minRelease, true)
      : undefined;

    if (endDate && endDate.getTime() < startDate.getTime()) {
      endDate = endOfDay(startDate);
    }

    return {
      startDate,
      startHour: config.maxRelease?.hour === "23:59" ? undefined : config.maxRelease?.hour,
      endDate: endDate,
      endHour: config.minRelease?.hour === "23:59" ? undefined : config.minRelease?.hour,
      penalty: this.calculatePenalty(config.penalty, config.type),
      type: config.type,
      penaltyBase: config.penalty,
      canCancel: config.cancelOnline,
    };
  }

  private buildMultipleBrackets(
    configurations: CancellationPolicyConfiguration[]
  ): PenaltyBracket[] {
    const penaltyBrackets = configurations.map(config => {
      const isStartDurationValid =
        config.maxRelease?.duration !== undefined &&
        !Number.isNaN(config.maxRelease?.duration) &&
        Number.isFinite(config.maxRelease?.duration);

      const isEndDurationValid =
        config.minRelease?.duration !== undefined &&
        !Number.isNaN(config.minRelease?.duration) &&
        Number.isFinite(config.minRelease?.duration);

      return {
        startDate: isStartDurationValid
          ? this.calculatePolicyDate(config.maxRelease, false)
          : undefined,
        startHour: config.maxRelease?.hour === "23:59" ? undefined : config.maxRelease?.hour,
        endDate: isEndDurationValid ? this.calculatePolicyDate(config.minRelease, true) : undefined,
        endHour: config.minRelease?.hour === "23:59" ? undefined : config.minRelease?.hour,
        penalty: this.calculatePenalty(config.penalty, config.type),
        type: config.type,
        penaltyBase: config.penalty,
        canCancel: config.cancelOnline,
      };
    });

    return [...penaltyBrackets].sort((a, b) => {
      if (a.startDate === undefined) {
        return -1;
      }
      if (b.startDate === undefined) {
        return 1;
      }
      return a.startDate.getTime() - b.startDate.getTime();
    });
  }

  public buildPenaltyBrackets(): PenaltyBracket[] {
    if (this.configurations.length === 0) {
      return [];
    }

    // When we have only one bracket we always show the range start
    if (this.configurations.length === 1) {
      return [this.buildSingleBracket(this.configurations[0])];
    }

    // When we have multiple brackets we hide open-ended starts or ends
    // a bracket is open-ended when it has no duration defined for its min-release or max-release
    // Because 23:59 hour limit is implicit, we do not show it
    return this.buildMultipleBrackets(this.configurations);
  }

  public getPenaltyBrackets(): PenaltyBracket[] {
    return this.buildPenaltyBrackets();
  }
}
